import { useMemo, useState } from "react";
import { Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { toNum, isBlank, isKey, keyRanks, playerColIndex, rankStyle, minApps } from "@/lib/braggersStats";

const EMPTY = [];

export function BraggersStatsTable({ dataset, C, testid, onPlayer }) {
  const columns = dataset.columns || EMPTY;
  const rows = dataset.rows || EMPTY;
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ col: null, dir: "asc" });
  const pc = playerColIndex(columns);
  const keyCols = columns.filter(isKey);

  const numeric = useMemo(() => columns.map((_, ci) => {
    const vals = rows.map((r) => r[ci]).filter((v) => !isBlank(v));
    return vals.length > 0 && vals.every((v) => toNum(v) !== null);
  }), [columns, rows]);
  const min = minApps(dataset);
  const ranks = useMemo(() => keyRanks(columns, rows, min), [columns, rows, min]);

  const view = useMemo(() => {
    const term = q.trim().toLowerCase();
    let idx = rows.map((_, i) => i);
    if (term) idx = idx.filter((i) => rows[i].some((c) => String(c ?? "").toLowerCase().includes(term)));
    if (sort.col !== null) {
      const ci = sort.col;
      const m = sort.dir === "asc" ? 1 : -1;
      idx = [...idx].sort((a, b) => {
        const x = rows[a][ci], y = rows[b][ci];
        if (isBlank(x) || isBlank(y)) return isBlank(x) - isBlank(y);
        if (numeric[ci]) return (toNum(x) - toNum(y)) * m;
        return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: "base" }) * m;
      });
    }
    return idx;
  }, [rows, q, sort, numeric]);

  const toggle = (ci) => setSort((s) => (s.col === ci ? { col: ci, dir: s.dir === "asc" ? "desc" : "asc" } : { col: ci, dir: "asc" }));
  const Icon = (ci) => (sort.col !== ci ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown);

  return (
    <div data-testid={testid}>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5">
        <div className="max-w-3xl">
          <h3 className="text-2xl sm:text-3xl font-medium" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.goldLight }}>{dataset.title}</h3>
          {(dataset.notes || []).slice(1).map((n, i) => (
            <p key={i} className="text-sm mt-1.5 leading-relaxed" style={{ color: C.sub }} data-testid={`${testid}-note-${i}`}>{n}</p>
          ))}
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: C.silver }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by any value…" data-testid={`${testid}-search`}
            className="w-full rounded-sm pl-10 pr-4 py-2.5 text-sm outline-none transition-colors focus:border-[#D4AF37]"
            style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }} />
        </div>
      </div>
      {keyCols.length > 0 && (
        <p className="mb-3 text-xs flex flex-wrap items-center gap-2" style={{ color: C.sub }} data-testid={`${testid}-legend`}>
          <span className="inline-block h-3 w-3 rounded-[2px]" style={{ background: "rgba(212,175,55,0.5)" }} />
          Gold = top 3 in {keyCols.join(" · ")}{min > 0 && <span data-testid={`${testid}-min-rule`}>(min. {min} appearances)</span>}
          {onPlayer && <span style={{ color: C.silver }}>· tap a player's name for their full record</span>}
        </p>
      )}
      <div className="rounded-sm border overflow-auto max-h-[70vh]" style={{ borderColor: C.border, background: C.bg }}>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {columns.map((col, ci) => {
                const I = Icon(ci);
                return (
                  <th key={ci} onClick={() => toggle(ci)} data-testid={`${testid}-header-${ci}`} aria-sort={sort.col === ci ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                    className={`sticky top-0 px-3 py-3 font-semibold whitespace-nowrap cursor-pointer select-none transition-colors hover:text-[#F2E3A0] ${ci === 0 ? "left-0 z-30 text-left" : "z-20"} ${numeric[ci] ? "text-right" : "text-left"}`}
                    style={{ background: C.elevated, color: sort.col === ci ? C.gold : C.sub, borderBottom: `1px solid ${C.border}` }}>
                    <span className="inline-flex items-center gap-1.5">{col || "—"}<I className="h-3.5 w-3.5 shrink-0 opacity-70" /></span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {view.map((ri, pos) => {
              const r = rows[ri];
              return (
                <tr key={ri} data-testid={`${testid}-row-${pos}`} className="transition-colors hover:bg-[#151C18]">
                  {columns.map((_, ci) => {
                    const rank = ranks[ci]?.[ri];
                    const hl = rankStyle(rank, C);
                    return (
                      <td key={ci} data-top-rank={hl ? rank : undefined}
                        className={`px-3 py-2.5 whitespace-nowrap ${ci === 0 ? "sticky left-0 z-10 font-medium" : ""} ${numeric[ci] ? "text-right tabular-nums" : ""}`}
                        style={{ color: ci === 0 ? C.text : C.sub, borderBottom: `1px solid ${C.border}`, background: ci === 0 ? C.surface : undefined, ...(hl || {}) }}>
                        {ci === pc && onPlayer && !isBlank(r[ci]) ? (
                          <button type="button" onClick={() => onPlayer(r[ci])} data-testid={`${testid}-player-${pos}`}
                            className="text-left font-medium underline-offset-4 decoration-[#D4AF37] transition-colors hover:underline hover:text-[#F2E3A0]">
                            {r[ci]}
                          </button>
                        ) : r[ci]}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!view.length && <p className="p-6 text-center text-sm" style={{ color: C.sub }} data-testid={`${testid}-no-match`}>No rows match “{q}”.</p>}
      </div>
      <p className="mt-2 text-xs" style={{ color: C.silver }} data-testid={`${testid}-count`}>Showing {view.length} of {rows.length} rows · click any column header to sort</p>
    </div>
  );
}
